// "Open a file": from the device or from a link. Shown when the viewer is
// opened with nothing to show. Recent device files are remembered as metadata
// only, because a browser cannot reopen a file without a retained handle, and
// the page says so rather than pretending the row is a link.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button } from '../core/Button/Button.js';
import { TextInput } from '../inputs/TextInput/TextInput.js';
import { Checkbox } from '../inputs/Checkbox/Checkbox.js';
import { Dropzone } from '../dropzone/Dropzone.js';
import { FamilyIcon, IconCloud, IconLink, IconShield } from './icons.js';
import { formatBytes, hostOf, isAbsoluteHttpUrl } from './format.js';
import { fill, type FileViewerLabels } from './labels.js';
import { familyLabel } from './chrome.js';
import type { FileFamily, FileViewerSource } from './types.js';

const STORAGE_KEY = 'rr.file-viewer.recent';
const MAX_RECENT = 6;
const FAMILIES: FileFamily[] = ['pdf', 'audio', 'video', 'spreadsheet', 'presentation', 'document', 'code', 'image'];

interface RecentLocal { name: string; size: number; modified: number; at: number }
interface RecentLink { url: string; host: string; at: number }
interface Recent { local: RecentLocal[]; links: RecentLink[] }

function readRecent(): Recent {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { local: [], links: [] };
    const parsed = JSON.parse(raw) as Partial<Recent>;
    return { local: parsed.local ?? [], links: parsed.links ?? [] };
  } catch {
    return { local: [], links: [] };
  }
}

function writeRecent(next: Recent): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
}

export interface FileSourcePickerProps {
  labels: FileViewerLabels;
  maxLocalBytes: number;
  onPick: (source: FileViewerSource) => void;
}

export function FileSourcePicker({ labels, maxLocalBytes, onPick }: FileSourcePickerProps): ReactNode {
  const [url, setUrl] = useState('');
  const [credentials, setCredentials] = useState(false);
  const [remember, setRemember] = useState(true);
  const [urlError, setUrlError] = useState<string | null>(null);
  const [recent, setRecent] = useState<Recent>({ local: [], links: [] });

  useEffect(() => { setRecent(readRecent()); }, []);

  const pickLocal = useCallback((files: File[]) => {
    const file = files[0];
    if (!file) return;
    const next: Recent = {
      ...readRecent(),
      local: [{ name: file.name, size: file.size, modified: file.lastModified, at: Date.now() }, ...readRecent().local.filter((r) => r.name !== file.name)].slice(0, MAX_RECENT)
    };
    writeRecent(next);
    onPick({ kind: 'local', file });
  }, [onPick]);

  const openLink = useCallback((raw: string, withCredentials: boolean) => {
    const value = raw.trim();
    if (!isAbsoluteHttpUrl(value)) { setUrlError(labels.pickerInvalidUrl); return; }
    setUrlError(null);
    if (remember) {
      const prev = readRecent();
      writeRecent({ ...prev, links: [{ url: value, host: hostOf(value), at: Date.now() }, ...prev.links.filter((l) => l.url !== value)].slice(0, MAX_RECENT) });
    }
    onPick({ kind: 'remote', url: value, credentials: withCredentials ? 'include' : 'omit' });
  }, [labels.pickerInvalidUrl, onPick, remember]);

  const clearRecent = () => { writeRecent({ local: [], links: [] }); setRecent({ local: [], links: [] }); };

  return (
    <div className="rr-fv__picker">
      <h2 className="rr-fv__picker-title">{labels.pickerTitle}</h2>

      <section className="rr-fv__picker-card rr-fv__picker-families">
        <div className="rr-fv__picker-card-title">{labels.pickerFamilies}</div>
        <ul className="rr-fv__family-row">
          {FAMILIES.map((f) => (
            <li key={f}><span className={`rr-fv__tile rr-fv__tile--${f}`}><FamilyIcon family={f} /></span>{familyLabel(labels, f)}</li>
          ))}
        </ul>
      </section>

      <div className="rr-fv__picker-grid">
        <section className="rr-fv__picker-card">
          <div className="rr-fv__picker-card-title">{labels.pickerFromDevice}</div>
          <Dropzone onDrop={pickLocal} multiple={false} maxSize={maxLocalBytes} maxFiles={1}>
            <div className="rr-fv__drop">
              <span className="rr-fv__drop-icon"><IconCloud size={40} /></span>
              <div className="rr-fv__drop-title">{labels.pickerDrop}</div>
              <div className="rr-fv__drop-or">{labels.pickerOr}</div>
              <Button type="button" variant="outline" size="sm">{labels.pickerChoose}</Button>
            </div>
          </Dropzone>
          <div className="rr-fv__picker-subtitle">{labels.pickerRecentLocal}</div>
          {recent.local.length === 0 ? (
            <div className="rr-fv__picker-empty">{labels.pickerNoRecent}</div>
          ) : (
            <table className="rr-fv__recent">
              <thead><tr><th>{labels.pickerName}</th><th>{labels.pickerSize}</th><th>{labels.pickerModified}</th></tr></thead>
              <tbody>
                {recent.local.map((r) => (
                  <tr key={`${r.name}-${r.at}`} title={labels.pickerReopenNote}>
                    <td>{r.name}</td><td>{formatBytes(r.size)}</td><td>{new Date(r.modified).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="rr-fv__picker-note"><IconShield size={14} /> {labels.pickerLocalNote}</div>
          {recent.local.length > 0 && <div className="rr-fv__picker-note">{labels.pickerReopenNote}</div>}
        </section>

        <section className="rr-fv__picker-card">
          <div className="rr-fv__picker-card-title">{labels.pickerFromLink}</div>
          <TextInput
            label={labels.pickerUrl}
            placeholder={labels.pickerUrlPlaceholder}
            value={url}
            error={urlError ?? undefined}
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => { setUrl(e.currentTarget.value); if (urlError) setUrlError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openLink(url, credentials); } }}
          />
          <Button type="button" fullWidth onClick={() => openLink(url, credentials)} disabled={!url.trim()}>{labels.pickerOpenLink}</Button>
          <Checkbox label={labels.pickerSendCredentials} description={labels.pickerSendCredentialsHint} checked={credentials} onChange={setCredentials} />
          <Checkbox label={labels.pickerRemember} checked={remember} onChange={setRemember} />
          <div className="rr-fv__picker-subtitle">{labels.pickerRecentLinks}</div>
          {recent.links.length === 0 ? (
            <div className="rr-fv__picker-empty">{labels.pickerNoRecent}</div>
          ) : (
            <table className="rr-fv__recent">
              <thead><tr><th>{labels.pickerLink}</th><th>{labels.pickerLastOpened}</th></tr></thead>
              <tbody>
                {recent.links.map((l) => (
                  <tr key={l.url}>
                    <td>
                      <button type="button" className="rr-fv__recent-link" onClick={() => openLink(l.url, false)} title={l.url}>
                        <IconLink size={14} /> {l.host || l.url}
                      </button>
                    </td>
                    <td>{new Date(l.at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="rr-fv__picker-note"><IconShield size={14} /> {labels.pickerRemoteNote}</div>
        </section>
      </div>

      <div className="rr-fv__picker-foot">
        <span>{fill(labels.pickerSizeNote, { size: formatBytes(maxLocalBytes) })}</span>
        {(recent.local.length > 0 || recent.links.length > 0) && (
          <Button type="button" variant="subtle" size="xs" onClick={clearRecent}>{labels.pickerClearRecent}</Button>
        )}
      </div>
    </div>
  );
}
